/**
 * Phase 21.4 Batch 3D — Public Contest detail (/contests/:slug).
 *
 * Renders banner, rules, judging criteria, countdown, reward breakdown,
 * and a context-aware primary CTA:
 *   - Logged-out       → "Log in to enter" (routes to /login?next=/contests/…/enter)
 *   - Open, no entry   → "Enter Contest"
 *   - Open, at cap     → "You've reached the max entries" (disabled)
 *   - Deadline passed  → "Entries closed"
 *   - Coming soon      → shows opens-in countdown
 *   - Judging / closed → hides Enter button
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SharedNavigation from "@/components/SharedNavigation";
import ContestStatusBadge from "@/components/contests/ContestStatusBadge";
import ContestCountdown from "@/components/contests/ContestCountdown";
import { entryDisplayStatus, canEnterContest, contestCountdownTarget, deriveContestDisplayStatus, formatFullDate } from "@/lib/contestStatus";
import ReactMarkdown from "react-markdown";
import api, { BACKEND_URL } from "@/lib/api";
import { getCoverUrl, getDefaultCover, optimizeImage } from "@/lib/mediaUrls";
import { toast } from "sonner";
import { useShareVisibility } from "@/lib/useShareVisibility";

const ContestDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myEntries, setMyEntries] = useState([]);
  const [enabled, setEnabled] = useState(null);
  // iter_220 — Fee visibility: fetch the artist's coin balance so we can
  // render a prominent "Entry Fee: 🪙 X · Your balance: 🪙 Y" panel and
  // enrich the primary CTA with the fee amount.
  const [coinBalance, setCoinBalance] = useState(null);

  const isAuthed = typeof window !== "undefined" && !!localStorage.getItem("session_token");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/contests/${slug}`);
      setContest(r.data?.contest || null);
      setEnabled(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "contests_disabled") {
        setEnabled(false);
      } else if (e?.response?.status === 404) {
        setContest(null);
        setEnabled(true);
      } else {
        toast.error("Failed to load contest");
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const loadMyEntries = useCallback(async () => {
    if (!isAuthed || !contest?.contest_id) return;
    try {
      const r = await api.get(`/contests/${contest.contest_id}/my-entry`);
      setMyEntries(r.data?.entries || []);
    } catch { /* ignore — non-critical */ }
  }, [contest?.contest_id, isAuthed]);

  // Phase 21.4 Batch 7 — Public list of entries in this contest so
  // artists can see who else is entered. NO admin_notes surfaced.
  const [publicEntries, setPublicEntries] = useState([]);
  const loadPublicEntries = useCallback(async () => {
    if (!contest?.contest_id) return;
    try {
      const r = await api.get(`/contests/${contest.contest_id}/entries`, {
        params: { limit: 60 },
      });
      setPublicEntries(r.data?.entries || []);
    } catch { /* silent */ }
  }, [contest?.contest_id]);

  // Phase 21.4 Production fix — Dedicated Winners podium so it appears
  // immediately after Commit Awards (independent of the entries cache).
  const [winners, setWinners] = useState([]);
  const loadWinners = useCallback(async () => {
    if (!contest?.contest_id) return;
    try {
      const r = await api.get(`/contests/${contest.contest_id}/winners`);
      setWinners(r.data?.winners || []);
    } catch { /* silent */ }
  }, [contest?.contest_id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMyEntries(); }, [loadMyEntries]);
  useEffect(() => { loadPublicEntries(); }, [loadPublicEntries]);
  useEffect(() => { loadWinners(); }, [loadWinners]);

  // iter_220 — Fetch wallet balance once when authed so the fee panel
  // and CTA can show insufficient-balance state.
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/wallet/me");
        const w = r.data?.wallet || r.data || {};
        const bal = w.balance ?? w.total ?? 0;
        if (!cancelled) setCoinBalance(bal);
      } catch {
        if (!cancelled) setCoinBalance(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthed]);

  // Live-refresh Winners + Entries the instant an admin commits awards.
  useEffect(() => {
    const handler = () => {
      load();
      loadWinners();
      loadPublicEntries();
      loadMyEntries();
    };
    window.addEventListener("rgm.contest.committed", handler);
    return () => window.removeEventListener("rgm.contest.committed", handler);
  }, [load, loadWinners, loadPublicEntries, loadMyEntries]);

  const canEnter = useMemo(() => canEnterContest(contest), [contest]);
  const displayStatus = useMemo(() => (contest ? deriveContestDisplayStatus(contest) : null), [contest]);
  const countdown = useMemo(() => (contest ? contestCountdownTarget(contest) : null), [contest]);

  const maxEntries = Number(contest?.max_entries_per_user || 1);
  const activeEntries = myEntries.filter((e) => e.status !== "withdrawn");
  const atCap = activeEntries.length >= maxEntries;
  const winnerSlots = Number(contest?.winner_slots || 3);
  const winnerPositions = winnerSlots === 1 ? ["1"] : ["1", "2", "3"];

  // Feb 2026 — Share button gated by the CEO visibility switch.
  const { canShare } = useShareVisibility();
  const handleShareContest = async () => {
    if (!contest) return;
    const slug = contest.slug || contest.contest_id;
    const canonical = `${BACKEND_URL}/api/share/contest/${slug}`;
    const title = contest.title || "RGM Contest";
    try {
      if (navigator.share) {
        await navigator.share({ title, url: canonical });
        return;
      }
      await navigator.clipboard.writeText(canonical);
      toast.success("Contest link copied!");
    } catch {
      toast.error("Could not share");
    }
  };
  // iter_220 — Fee-aware entry gating on the detail page.
  const entryFee = Number(contest?.entry_fee_coins || 0);
  const isPaidEntry = entryFee > 0;
  const balanceKnown = isAuthed && coinBalance != null;
  const insufficient = isPaidEntry && balanceKnown && coinBalance < entryFee;

  const primaryCta = (variant = "primary") => {
    const suffix = variant === "primary" ? "" : `-${variant}`;
    if (!isAuthed) {
      const next = `/contests/${slug}/enter`;
      return (
        <Link
          to={`/login?next=${encodeURIComponent(next)}`}
          className="inline-flex items-center justify-center px-6 py-2.5 rounded-md bg-white text-black hover:bg-amber-200 font-black w-full sm:w-auto"
          data-testid={`contest-detail-login-cta${suffix}`}
        >
          Log in to enter
        </Link>
      );
    }
    if (displayStatus?.key === "coming_soon") {
      return (
        <Button disabled className="bg-white/10 text-white/60 font-black w-full sm:w-auto px-6 cursor-not-allowed" data-testid={`contest-detail-coming-soon${suffix}`}>
          Not open yet
        </Button>
      );
    }
    if (displayStatus?.key === "deadline_passed") {
      return (
        <Button disabled className="bg-white/10 text-white/60 font-black w-full sm:w-auto px-6 cursor-not-allowed" data-testid={`contest-detail-deadline-passed${suffix}`}>
          Entries closed
        </Button>
      );
    }
    if (displayStatus?.key === "judging" || displayStatus?.key === "closed") {
      // Enter button hidden entirely
      return null;
    }
    if (atCap) {
      return (
        <Button disabled className="bg-white/10 text-white/60 font-black w-full sm:w-auto px-6 cursor-not-allowed" data-testid={`contest-detail-at-cap${suffix}`}>
          Max entries reached ({activeEntries.length}/{maxEntries})
        </Button>
      );
    }
    if (canEnter) {
      // iter_220 — Block entry attempts when balance < fee so the artist
      // never reaches the entry form only to be rejected server-side.
      if (isPaidEntry && insufficient) {
        return (
          <Link
            to="/wallet"
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-md bg-amber-500 text-black hover:bg-amber-400 font-black w-full sm:w-auto"
            data-testid={`contest-detail-get-coins${suffix}`}
          >
            🪙 Get More Coins — Need {entryFee}
          </Link>
        );
      }
      return (
        <Link
          to={`/contests/${slug}/enter`}
          className="inline-flex items-center justify-center px-6 py-2.5 rounded-md bg-white text-black hover:bg-amber-200 font-black w-full sm:w-auto"
          data-testid={`contest-detail-enter-cta${suffix}`}
        >
          {isPaidEntry ? `Enter Contest — 🪙 ${entryFee} →` : "Enter Contest →"}
        </Link>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation />
        <p className="pt-24 px-4 text-gray-500" data-testid="contest-detail-loading">Loading contest…</p>
      </div>
    );
  }

  if (enabled === false) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation />
        <div className="max-w-3xl mx-auto pt-24 px-4 text-center" data-testid="contest-detail-flag-off">
          <p className="text-5xl mb-3">🏆</p>
          <h1 className="text-3xl font-black mb-2">Contests are coming soon</h1>
          <p className="text-sm text-gray-400 mb-4">Follow @RGMStudio for the launch announcement.</p>
          <Link to="/contests" className="text-purple-300 underline">← Back to Contest Center</Link>
        </div>
      </div>
    );
  }

  if (!contest) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation />
        <div className="max-w-3xl mx-auto pt-24 px-4 text-center" data-testid="contest-detail-not-found">
          <p className="text-5xl mb-3">🤔</p>
          <h1 className="text-3xl font-black mb-2">Contest not found</h1>
          <p className="text-sm text-gray-400 mb-4">This contest may have been removed or the link is incorrect.</p>
          <Link to="/contests" className="text-purple-300 underline">← Back to Contest Center</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="contest-detail-page">
      <SharedNavigation />

      {/* Banner */}
      <div className="relative w-full h-56 sm:h-72 lg:h-96 overflow-hidden pt-16">
        <div className="absolute inset-0 pt-16">
          {contest.banner_image ? (
            <img src={optimizeImage(contest.banner_image, 'cover')} alt={contest.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-purple-900 via-black to-amber-900" />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <ContestStatusBadge contest={contest} size="lg" />
              <span className="text-[10px] uppercase tracking-widest text-white/80 px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 font-black">
                {contest.entry_type === "beat" ? "🎧 Beat Contest" : "🎤 Track Contest"}
              </span>
              {canShare("contests") && (
                <button
                  type="button"
                  onClick={handleShareContest}
                  className="ml-auto text-[11px] font-bold px-3 py-1 rounded-full border border-purple-400/40 text-purple-100 bg-purple-500/10 hover:bg-purple-500/20"
                  data-testid="contest-detail-share-btn"
                  title="Share this contest"
                >
                  ↗ Share
                </button>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight max-w-3xl" data-testid="contest-detail-title">
              {contest.title}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* iter_220 — Fee panel: prominently show entry fee + user's
            balance BEFORE the CTA row so artists know the cost before
            clicking Enter Contest. Rendered whenever the contest has a
            coin fee, regardless of contest status (helps set
            expectations even on coming-soon / judging contests). */}
        {isPaidEntry && (
          <section
            className={`rounded-2xl border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${
              insufficient
                ? "border-rose-500/40 bg-rose-500/10"
                : "border-amber-500/40 bg-amber-500/10"
            }`}
            data-testid="contest-detail-fee-panel"
          >
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">
                🪙 Entry Fee
              </p>
              <p className="text-2xl sm:text-3xl font-black text-white mt-1" data-testid="contest-detail-fee-amount">
                🪙 {entryFee.toLocaleString()} RGM Coins
              </p>
              {isAuthed ? (
                <p className="text-xs text-gray-200 mt-1">
                  Your balance:{" "}
                  <span
                    className={insufficient ? "text-rose-200 font-black" : "text-white font-black"}
                    data-testid="contest-detail-user-balance"
                  >
                    🪙 {coinBalance != null ? coinBalance.toLocaleString() : "—"}
                  </span>
                  {insufficient && (
                    <span className="ml-2 text-rose-200 font-black" data-testid="contest-detail-insufficient">
                      · Not enough coins to enter
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-xs text-gray-300 mt-1">Log in to see your balance.</p>
              )}
            </div>
            {insufficient && (
              <Link
                to="/wallet"
                className="px-3 py-2 rounded-lg bg-amber-500 text-black font-black text-xs hover:bg-amber-400 shrink-0"
                data-testid="contest-detail-fee-get-coins"
              >
                Get More Coins →
              </Link>
            )}
          </section>
        )}

        {/* Phase 21.4 Production fix — Winners podium (renders immediately post-Commit Awards) */}
        {winners.length > 0 && (
          <section
            className="rounded-2xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-900/30 via-purple-900/20 to-black p-5 sm:p-6"
            data-testid="contest-detail-winners"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🏆 Contest Winners</p>
                <h2 className="text-lg sm:text-xl font-black">The Podium</h2>
              </div>
              <Link
                to="/hall-of-champions"
                className="text-[11px] text-purple-300 underline hover:text-purple-100"
                data-testid="contest-detail-winners-hof-link"
              >
                See Hall of Champions →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {winners.map((w) => {
                const meta = {
                  1: { emoji: "🥇", label: "1st Place", ring: "ring-amber-400" },
                  2: { emoji: "🥈", label: "2nd Place", ring: "ring-gray-300" },
                  3: { emoji: "🥉", label: "3rd Place", ring: "ring-orange-400" },
                }[w.position] || { emoji: `#${w.position}`, label: `Position ${w.position}`, ring: "ring-white/20" };
                // iter_186 — Prefer iter_184's canonical 1500×1500
                // Cloudinary winner cover; fall back to the entry
                // snapshot cover_art for pre-iter_184 contests.
                const cover = w.winner_cover_url || w.ref_snapshot?.cover_art;
                const title = w.ref_snapshot?.title || "Winning entry";
                const artist = w.artist?.artist_name || "Artist";
                const trackHref = w.ref_id
                  ? (w.entry_type === "beat" ? `/beats/${w.ref_id}` : `/track/${w.ref_id}`)
                  : null;
                const Wrap = trackHref ? Link : "div";
                const wrapProps = trackHref ? { to: trackHref } : {};
                return (
                  <Wrap
                    key={w.entry_id}
                    {...wrapProps}
                    className={`block rounded-xl overflow-hidden border-2 border-amber-500/40 bg-black/50 hover:brightness-110 transition-all ${meta.ring} ring-1 ring-inset`}
                    data-testid={`contest-detail-winner-${w.position}`}
                  >
                    <div className="aspect-square bg-gray-900 relative overflow-hidden">
                      {cover ? (
                        <img src={cover} alt={title} className="w-full h-full object-cover" data-testid={`contest-detail-winner-cover-${w.position}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-5xl">🎤</div>
                      )}
                      <span className="absolute top-2 left-2 text-3xl drop-shadow-lg" data-testid={`contest-detail-winner-emoji-${w.position}`}>
                        {meta.emoji}
                      </span>
                    </div>
                    <div className="p-3">
                      <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">{meta.label}</p>
                      <p className="text-white font-black text-sm truncate mt-0.5" data-testid={`contest-detail-winner-title-${w.position}`}>
                        {title}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {w.artist?.profile_photo && (
                          <img src={optimizeImage(w.artist.profile_photo, 'avatar')} alt="" className="w-5 h-5 rounded-full object-cover border border-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        )}
                        <p className="text-[11px] text-gray-300 truncate flex-1" data-testid={`contest-detail-winner-artist-${w.position}`}>
                          {artist}
                        </p>
                      </div>
                    </div>
                  </Wrap>
                );
              })}
            </div>
          </section>
        )}

        {/* CTA + countdown row */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 sm:p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
          <div className="flex-1">
            {contest.description && (
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed" data-testid="contest-detail-description">
                {contest.description}
              </p>
            )}
            {isAuthed && myEntries.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="contest-detail-my-entries-summary">
                <span className="text-[11px] font-black uppercase tracking-wider text-gray-400">Your entries:</span>
                {myEntries.map((e) => {
                  const es = entryDisplayStatus(e);
                  return (
                    <span key={e.entry_id} className={`text-[11px] font-black px-2 py-0.5 rounded-full border ${es.tone}`} data-testid={`contest-detail-my-entry-${e.entry_id}`}>
                      {es.label}
                    </span>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-2" data-testid="contest-detail-entries-cap-label">
              Max entries per artist: <span className="text-white font-black">{maxEntries}</span>
              {isAuthed && ` · You've used ${activeEntries.length}`}
            </p>
          </div>
          <div className="flex flex-col items-start lg:items-end gap-3">
            {countdown && (
              <ContestCountdown
                targetIso={countdown.target}
                label={countdown.label}
              />
            )}
            {primaryCta()}
          </div>
        </div>

        {/* Batch 3E — Explicit Key Dates row (Start / Deadline / End / Results) */}
        {(contest.starts_at || contest.submission_deadline || contest.ends_at || contest.results_at) && (
          <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6" data-testid="contest-detail-dates">
            <h2 className="text-lg sm:text-xl font-black mb-3">📅 Key Dates</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {contest.starts_at && (
                <div className="rounded-lg border border-white/10 bg-black/40 p-3" data-testid="contest-detail-date-start">
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-black">Start Date</p>
                  <p className="text-white mt-0.5">{formatFullDate(contest.starts_at)}</p>
                </div>
              )}
              {contest.submission_deadline && (
                <div className="rounded-lg border border-white/10 bg-black/40 p-3" data-testid="contest-detail-date-deadline">
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">Submission Deadline</p>
                  <p className="text-white mt-0.5">{formatFullDate(contest.submission_deadline)}</p>
                </div>
              )}
              {contest.ends_at && (
                <div className="rounded-lg border border-white/10 bg-black/40 p-3" data-testid="contest-detail-date-ends">
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-black">Contest End Date</p>
                  <p className="text-white mt-0.5">{formatFullDate(contest.ends_at)}</p>
                </div>
              )}
              {contest.results_at && (
                <div className="rounded-lg border border-white/10 bg-black/40 p-3" data-testid="contest-detail-date-results">
                  <p className="text-[10px] uppercase tracking-widest text-purple-300 font-black">Results Date</p>
                  <p className="text-white mt-0.5">{formatFullDate(contest.results_at)}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Rewards */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6" data-testid="contest-detail-rewards">
          <h2 className="text-lg sm:text-xl font-black mb-1">🏅 Rewards</h2>
          <p className="text-[11px] text-gray-400 mb-4">
            Reward coins land in your <span className="text-emerald-300 font-black">reward balance</span> — spendable on RGM features but not withdrawable to cash.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {winnerPositions.map((pos) => {
              const cfg = (contest.rewards || {})[pos] || {};
              const emoji = pos === "1" ? "🥇" : pos === "2" ? "🥈" : "🥉";
              const label = pos === "1" ? "1st Place" : pos === "2" ? "2nd Place" : "3rd Place";
              return (
                <div
                  key={pos}
                  className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-900/25 to-black p-4"
                  data-testid={`contest-detail-reward-${pos}`}
                >
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">{emoji} {label}</p>
                  <p className="text-2xl font-black text-white mt-1">🪙 {Number(cfg.coins || 0).toLocaleString()}</p>
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-black">Reward coins</p>
                  {contest.entry_type === "track" && Number(cfg.promo_plays || 0) > 0 && (
                    <p className="text-xs text-purple-200 mt-2">⚡ {Number(cfg.promo_plays).toLocaleString()} promo plays</p>
                  )}
                  {(cfg.badges || []).length > 0 && (
                    <p className="text-[10px] text-amber-200 mt-2">🎖 {cfg.badges.length} badge{cfg.badges.length > 1 ? "s" : ""}</p>
                  )}
                </div>
              );
            })}
          </div>
          {contest.rewards?.participation && (Number(contest.rewards.participation.coins || 0) > 0 || Number(contest.rewards.participation.promo_plays || 0) > 0) && (
            <div className="mt-3 rounded-xl border border-white/10 bg-black/40 p-3" data-testid="contest-detail-reward-participation">
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-black">🎉 Participation (everyone who enters)</p>
              <p className="text-sm text-white mt-0.5">
                {Number(contest.rewards.participation.coins || 0) > 0 && `🪙 ${Number(contest.rewards.participation.coins).toLocaleString()} coins`}
                {Number(contest.rewards.participation.promo_plays || 0) > 0 && contest.entry_type === "track" && ` · ⚡ ${Number(contest.rewards.participation.promo_plays).toLocaleString()} promo plays`}
              </p>
            </div>
          )}
        </section>

        {/* Rules */}
        {contest.rules_md && (
          <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6" data-testid="contest-detail-rules">
            <h2 className="text-lg sm:text-xl font-black mb-3">📜 Rules</h2>
            <div className="prose prose-sm prose-invert max-w-none text-gray-200
                            prose-headings:text-white prose-headings:font-black
                            prose-a:text-purple-300 prose-a:underline
                            prose-strong:text-white
                            prose-ul:list-disc prose-ol:list-decimal
                            prose-li:my-0.5 prose-p:my-2"
                 data-testid="contest-detail-rules-body">
              <ReactMarkdown
                disallowedElements={["script", "iframe", "style", "img"]}
                unwrapDisallowed
              >
                {contest.rules_md}
              </ReactMarkdown>
            </div>
          </section>
        )}

        {/* Judging criteria */}
        {(contest.judging_criteria || []).length > 0 && (
          <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6" data-testid="contest-detail-criteria">
            <h2 className="text-lg sm:text-xl font-black mb-3">🎯 Judging Criteria</h2>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-200">
              {contest.judging_criteria.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Phase 21.4 Batch 7 — Public entries list */}
        {publicEntries.length > 0 && (
          <section
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
            data-testid="contest-detail-entries"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h2 className="text-lg sm:text-xl font-black">
                🎤 Submitted Entries
                <span className="ml-2 text-xs text-gray-400 font-black">
                  ({publicEntries.length})
                </span>
              </h2>
              <p className="text-[11px] text-gray-500">
                Public entries — see who else is competing.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {publicEntries.map((e) => {
                // Canonical cover resolution — accept both cover_url
                // and cover_art on the snapshot and run through the
                // branded placeholder resolver so stale bare-filename
                // records show the RGM placeholder instead of a
                // broken <img> (microphone fallback).
                const _snap = e.ref_snapshot || {};
                const _rawCover = _snap.cover_url || _snap.cover_art;
                const cover = _rawCover
                  ? getCoverUrl({ cover_url: _rawCover, cover_art: _snap.cover_art })
                  : null;
                const title = _snap.title || "Untitled";
                const artist = e.artist?.artist_name || "Anonymous";
                const isWinner = e.status === "winner" && e.position;
                // Phase 21.4 Batch 10 — clickable to source track/beat.
                const entryHref = e.entry_type === "beat"
                  ? (e.ref_id ? `/beats/${e.ref_id}` : null)
                  : (e.ref_id ? `/track/${e.ref_id}` : null);
                const CardWrap = entryHref ? Link : "div";
                const cardProps = entryHref ? { to: entryHref } : {};
                return (
                  <CardWrap
                    key={e.entry_id}
                    {...cardProps}
                    className={`block rounded-xl overflow-hidden border ${isWinner ? "border-amber-400/60 bg-amber-500/10" : "border-white/10 bg-black/40"} hover:border-purple-400/60 transition-all`}
                    data-testid={`contest-detail-entry-${e.entry_id}`}
                  >
                    <div className="aspect-square bg-gray-900 relative overflow-hidden">
                      {cover ? (
                        <img
                          src={cover}
                          alt={title}
                          className="w-full h-full object-cover"
                          onError={(ev) => {
                            ev.currentTarget.style.display = "none";
                            const fb = ev.currentTarget.nextElementSibling;
                            if (fb) fb.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className="absolute inset-0 items-center justify-center text-4xl"
                        style={{ display: cover ? "none" : "flex" }}
                      >
                        {e.entry_type === "beat" ? "🎧" : "🎤"}
                      </div>
                      {isWinner && (
                        <span className="absolute top-1.5 left-1.5 text-[10px] font-black bg-black/80 text-amber-300 px-2 py-0.5 rounded-full border border-amber-400/60">
                          {e.position === 1 ? "🥇" : e.position === 2 ? "🥈" : "🥉"} #{e.position}
                        </span>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-white font-black text-xs truncate" title={title}>
                        {title}
                      </p>
                      <p className="text-[10px] text-purple-200 truncate" title={artist}>
                        by {artist}
                      </p>
                    </div>
                  </CardWrap>
                );
              })}
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <div className="flex flex-wrap gap-3 justify-center pt-2">
          {primaryCta("bottom")}
          <Link
            to="/contests"
            className="inline-flex items-center px-4 py-2 rounded-md bg-white/10 border border-white/15 text-white text-sm font-black hover:bg-white/20 transition-colors"
            data-testid="contest-detail-back"
          >
            ← All contests
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ContestDetailPage;
